<template>
  <FiltersItem>
    <template #title>
      {{ $l('showresult.filter.travelAgencyFilterTitle') }}
    </template>
    <template #content>
      <FiltersItemContent>
        <template #content>
          <BaseToggle
            :checked="checked"
            :text="$l('showresult.filter.hideForeignAgency')"
            @update:checked="toggleForeign"
          />
        </template>
      </FiltersItemContent>
      <FiltersItemContent>
        <template #title>
          {{ $l('global.simplePhrases.travelCompanies') }}
          <span
            v-if="isAgentsFiltered"
            class="filtersTitle__reset"
            @click="toggleAll"
          >
            {{ $l('showresult.filter.resetTitle') }}
          </span>
        </template>
        <template #content>
          <FiltersList
            :items="agencies"
            @itemClicked="onAgencyClicked"
            @exclusiveClicked="onExclusiveClick"
          />
        </template>
      </FiltersItemContent>
    </template>

    <template #summary>
      <FiltersItemSummary :items="summaryItems" />
    </template>
  </FiltersItem>
</template>

<script setup>
import { arrayToggleValue, l, lreplace } from '@utils/toolbox';
import { useFiltersStore } from './store/filtersStore';
import { useResultQueryStore } from './store/resultQueryStore';

const filtersStore = useFiltersStore();
const resultQueryStore = useResultQueryStore();

const noAgentsSelected = computed(() => filtersStore.state.enabledAgents.length === 0);

const travelAgentsTransformed = computed(() => {
	const travelAgents = resultQueryStore.state?.response?.travelAgent;
	return Array.isArray(travelAgents) ? travelAgents : Object.values(travelAgents || {});
});

const { summaryItems } = useSummary();
function useSummary () {
	const summaryItems = computed(() => {
		const summary = [];

		if (filtersStore.state.foreign) {
			summary.push(l('showresult.filter.summary.onlyShowLocalTravelAgencies'));
		}

		if (filtersStore.state.enabledAgents.length) {
			const agenciesArr = filtersStore.state.enabledAgents.map(agency => js_params.module.Filters.agencies[agency]);
			const agenciesStr = agenciesArr.join(', ').trim();
			summary.push(lreplace('showresult.filter.summary.excludeTravelAgencies', { agencies: agenciesStr }));
		}

		return summary;
	});

	return {
		summaryItems
	};
}

const { toggleAll, isAgentsFiltered } = useAgenciesToggleAll();
function useAgenciesToggleAll () {
	function toggleAll () {
		if (noAgentsSelected.value) {
			return;
		}
		filtersStore.set({ key: 'enabledAgents', value: [] });
	}

	const isAgentsFiltered = computed(() => filtersStore.state.enabledAgents.length > 0);

	return {
		toggleAll,
		isAgentsFiltered
	};
}

// Foreign agencies
const { checked, toggleForeign } = useForeignAgency();
function useForeignAgency () {
	// For some reason resultQueryStore.state.response.foreign is sometimes an object and sometimes an array
	// This is a workaround to make sure it's always an array
	const foreignAgencies = computed(() => {
		const foreign = resultQueryStore.state.response.foreign;
		return Array.isArray(foreign) ? foreign : Object.keys(foreign);
	});

	const checked = computed(() => foreignAgencies.value.every(agency => !filtersStore.state.enabledAgents.includes(agency)) && filtersStore.state.enabledAgents.length > 0);

	function toggleForeign () {
		const { enabledAgents } = filtersStore.state;
		// Can be an object or an array, depending on the API response
		// This is a workaround to make sure it's always an array
		const allTravelAgents = travelAgentsTransformed.value;

		// If 'checked' is true, we want to add all foreign agencies to enabledAgents
		if (checked.value) {
			const updatedAgents = enabledAgents.concat(foreignAgencies.value);

			// If adding all foreign agencies results in every single agent being selected,
			// reset to an empty array instead (meaning all disabled).
			if (updatedAgents.length === allTravelAgents.length) {
				filtersStore.set({ key: 'enabledAgents', value: [] });
			} else {
				filtersStore.set({ key: 'enabledAgents', value: updatedAgents });
			}
			return;
		}

		// If 'checked' is false, we need to remove foreign agencies.
		// If no agents are currently selected, then we want to enable all except foreign.
		if (enabledAgents.length === 0) {
			const nonForeignAgents = allTravelAgents.filter(agent => !foreignAgencies.value.includes(agent));
			filtersStore.set({ key: 'enabledAgents', value: nonForeignAgents });
			return;
		}

		// Otherwise, some agents are already selected, so remove foreign ones from the selection.
		const filteredAgents = enabledAgents.filter(agent => !foreignAgencies.value.includes(agent));
		filtersStore.set({ key: 'enabledAgents', value: filteredAgents });
	}


	return {
		checked,
		toggleForeign
	};
}

// Toggle individual agency
const { agencies, onItemClicked: onAgencyClicked, onExclusiveClick } = useAgencies();
function useAgencies () {
	const noAgenciesSelected = computed(() => filtersStore.state.enabledAgents.length === 0);

	const agencies = computed(() => {
		return travelAgentsTransformed.value.map((agency) => {
			return {
				text: js_params.module.Filters.agencies[agency],
				value: agency,
				isChecked: noAgenciesSelected.value || filtersStore.state.enabledAgents.includes(agency)
			};
		}).sort((a, b) => a.text.localeCompare(b.text));
	});

	function onItemClicked (item) {
		if (noAgenciesSelected.value) {
			filtersStore.set({ key: 'enabledAgents', value: travelAgentsTransformed.value.filter(agency => item.value !== agency) });
		} else {
			let newAgencies = arrayToggleValue(filtersStore.state.enabledAgents, item.value);

			if (newAgencies.length === travelAgentsTransformed.value.length) {
				newAgencies = [];
			}
			filtersStore.set({ key: 'enabledAgents', value: newAgencies });
		}
	}


	function onExclusiveClick (item) {
		filtersStore.set({ key: 'enabledAgents', value: [item.value] });
	}


	return {
		agencies,
		onExclusiveClick,
		onItemClicked
	};
}


</script>
